// src/pages/OrderViewer/components/OrderDetailStep.tsx
import React from "react";
import { CircularProgress } from "@mui/material";
import CardSection from "../../../components/ui/CardSection";
import SummaryRow from "../../../components/ui/SummaryRow";
import Button from "../../../components/ui/Button";
import {
  getOrderStatusShort,
  formatDateTime,
} from "../../../helpers/order.helper";
import { formatBRL } from "../../../helpers/currency.helper";
import { OrderDetail } from "../types";
import StatusBadge from "./StatusBadge";
import { ErrorBanner, LoadingBox } from "../styles";
import {
  TopBar,
  TopBarTitle,
  OrderHeaderRow,
  OrderTitle,
  Meta,
  ProductRow,
  ProductName,
  ProductMeta,
  ProductNote,
  ProductTotal,
  TotalRow,
  TotalLabel,
  TotalValue,
} from "./OrderDetailStep.styles";

interface OrderDetailStepProps {
  order: OrderDetail | null;
  loading: boolean;
  error: string | null;
  onBackToList: () => void;
}

const OrderDetailStep: React.FC<OrderDetailStepProps> = ({
  order,
  loading,
  error,
  onBackToList,
}) => {
  return (
    <div>
      <TopBar>
        <TopBarTitle>Detalhes do pedido</TopBarTitle>
        <Button variant="ghost" size="sm" onClick={onBackToList}>
          Voltar para a lista
        </Button>
      </TopBar>

      {loading && (
        <LoadingBox>
          <CircularProgress size={28} />
        </LoadingBox>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {!loading && !error && !order && (
        <ErrorBanner $tone="info">
          Selecione um pedido na etapa anterior para ver os detalhes.
        </ErrorBanner>
      )}

      {!loading && !error && order && (
        <>
          <OrderHeaderRow>
            <OrderTitle>Pedido #{order.document}</OrderTitle>
            <StatusBadge status={order.status} />
          </OrderHeaderRow>
          <Meta>Criado em: {formatDateTime(order.createdAt)}</Meta>
          <Meta>Atualizado em: {formatDateTime(order.updateStatus)}</Meta>

          <CardSection icon="user" title="Dados do cliente / entrega">
            <SummaryRow label="Nome" value={order.deliveryName} />
            <SummaryRow label="Telefone" value={order.phone} />
            {order.deliveryType === "delivery" && (
              <>
                <SummaryRow
                  label="Endereço"
                  value={`${order.deliveryAddress}, ${order.deliveryNumber} - ${order.deliveryDistrict}`}
                />
                {order.deliveryComplement && (
                  <SummaryRow label="Complemento" value={order.deliveryComplement} />
                )}
                {order.deliveryReference && (
                  <SummaryRow label="Referência" value={order.deliveryReference} />
                )}
                <SummaryRow
                  label="Cidade"
                  value={`${order.deliveryCity.name} / ${order.deliveryCity.state}`}
                />
                <SummaryRow label="CEP" value={order.deliveryZipCode} />
              </>
            )}
            {order.note && <SummaryRow label="Observação do pedido" value={order.note} />}
          </CardSection>

          <CardSection icon="bag" title="Loja">
            <SummaryRow label="Nome" value={order.store.name} />
            <SummaryRow label="Telefone" value={order.store.phone} />
            <SummaryRow
              label="Endereço"
              value={`${order.store.address}, ${order.store.number} - ${order.store.district}`}
            />
            <SummaryRow
              label="Cidade"
              value={`${order.store.city.name} / ${order.store.city.state}`}
            />
          </CardSection>

          <CardSection icon="receipt" title="Itens do pedido">
            {order.products.map((p) => (
              <ProductRow key={p.id}>
                <div>
                  <ProductName>
                    {p.amount}x {p.name}
                  </ProductName>
                  <ProductMeta>
                    Valor unit.: {formatBRL(p.price)}
                    {p.addition ? ` · Adição: ${formatBRL(p.addition)}` : ""}
                    {p.discount ? ` · Desconto: ${formatBRL(p.discount)}` : ""}
                  </ProductMeta>
                  {p.note && <ProductNote>Obs: {p.note}</ProductNote>}
                </div>
                <ProductTotal>{formatBRL(p.total)}</ProductTotal>
              </ProductRow>
            ))}
          </CardSection>

          <CardSection icon="card" title="Pagamentos">
            {order.payments.map((pay) => (
              <SummaryRow
                key={pay.id}
                label={pay.money ? `${pay.name} (dinheiro)` : pay.name}
                value={formatBRL(pay.value)}
              />
            ))}
            <SummaryRow label="Taxa de entrega" value={formatBRL(order.tax)} />
            {order.voucherValue && (
              <SummaryRow
                label={`Voucher (${order.voucherType ?? ""})`}
                value={`-${formatBRL(order.voucherValue)}`}
              />
            )}
            <TotalRow>
              <TotalLabel>Total</TotalLabel>
              <TotalValue>{formatBRL(order.total)}</TotalValue>
            </TotalRow>
          </CardSection>

          <CardSection icon="clock" title="Histórico de status">
            {order.statuses.map((s, index) => (
              <SummaryRow
                key={index}
                label={getOrderStatusShort(s.status)}
                value={formatDateTime(s.createdAt)}
              />
            ))}
          </CardSection>
        </>
      )}
    </div>
  );
};

export default OrderDetailStep;
